import { useLocation, Link } from "@/lib/router-compat";
import { Home, Bookmark, Trophy, Wallet, Calendar, MessageSquare, Sparkles, GraduationCap, Podcast, Moon, Sun, Brain, Gamepad2, TrendingUp, Video } from "lucide-react";
import { Sidebar, SidebarContent, SidebarFooter, SidebarGroup, SidebarGroupContent, SidebarHeader, SidebarMenu, SidebarMenuButton, SidebarMenuItem } from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import { ProfileAvatar } from "@/components/ui/ProfileAvatar";
import ThemeLogo from "@/components/ui/ThemeLogo";
import { useAuth } from "@/context/AuthContext";
import { useProfile } from "@/hooks/useProfile";
import { Suspense, lazy, useState } from "react";
const AuthModal = lazy(() => import("@/components/auth/AuthModal"));
import { cn } from "@/lib/utils";
const DailyRecapSidebarPlayer = lazy(() => import("@/components/profile/DailyRecapSidebarPlayer"));
import PersistentPodcastPlayer from "@/components/podcast/PersistentPodcastPlayer";
import { usePodcastPlayer } from "@/context/PodcastPlayerContext";
import { useTheme } from "next-themes";
import { usePageVisibility } from "@/hooks/usePageVisibility";
import { SIDEBAR_URL_TO_PAGE_KEY } from "@/config/pageRegistry";
import { useT } from "@/i18n/useLocale";
import type { MessageKey } from "@/i18n/dictionaries";
import LocaleSwitcher from "@/components/i18n/LocaleSwitcher";


// Menu items for navigation
const menuItems = [{
  title: "nav.news",
  url: "/",
  icon: Home,
  emoji: "📰"
}, {
  title: "nav.weekly",
  url: "/weekly",
  icon: Calendar,
  emoji: "📰"
}, {
  title: "nav.podcasts",
  url: "/podcasts",
  icon: Podcast,
  emoji: "🎙️"
}, {
  title: "nav.social",
  url: "/social",
  icon: Video,
  emoji: "🎬"
}, {
  title: "nav.feed",
  url: "/feed",
  icon: MessageSquare,
  emoji: "💬"
}, {
  title: "nav.markets",
  url: "/markets",
  icon: Wallet,
  emoji: "🌐"
}, {
  title: "nav.predictionMarkets",
  url: "/prediction-markets",
  icon: TrendingUp,
  emoji: "🎯"
}, {
  title: "nav.intelligence",
  url: "/intelligence",
  icon: Brain,
  emoji: "🧠"
}, {
  title: "nav.leaderboard",
  url: "/leaderboard",
  icon: Trophy,
  emoji: "🏆"
}, {
  title: "nav.learn",
  url: "/learn",
  icon: GraduationCap,
  emoji: "🎓"
}, {
  title: "nav.games",
  url: "/games",
  icon: Gamepad2,
  emoji: "🎮"
}, {
  title: "nav.bookmarks",
  url: "/bookmarks",
  icon: Bookmark,
  emoji: "🔖"
}];
const AppSidebar = () => {
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const { profile } = useProfile();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const { currentEpisode } = usePodcastPlayer();
  const { theme, setTheme } = useTheme();
  const { isEnabled } = usePageVisibility();
  const t = useT();

  // Filter sidebar items using the central page registry via URL mapping.
  // Any sidebar url that maps to a known PageKey is hidden when that key is disabled.
  const AUTH_ONLY_URLS = ["/bookmarks", "/leaderboard"];
  const visibleMenuItems = menuItems.filter((item) => {
    if (!isAuthenticated && AUTH_ONLY_URLS.includes(item.url)) return false;
    const key = SIDEBAR_URL_TO_PAGE_KEY[item.url];
    if (!key) return true; // no key mapped -> always show (e.g. Home)
    return isEnabled(key);
  });

  
  return <>
      <div className="hidden md:block p-[15px] h-[calc(100dvh-4rem)] flex flex-col">
        <Sidebar variant="sidebar" className={cn("!top-8 !h-[calc(100svh-68px)] rounded-[18px] backdrop-blur-[20px] shadow-sidebar", "bg-glass-light dark:bg-glass-dark", "border border-border/30", "w-60")}>
        <SidebarHeader className="h-16 px-3 flex flex-row items-center justify-between border-b border-border/30">
          <ThemeLogo className="h-8 shrink-0" />
        </SidebarHeader>

        <SidebarContent className="px-2 py-3 overflow-hidden">
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu className="space-y-0.5">
                {visibleMenuItems.map(item => {
                  const isActive = location.pathname === item.url;
                  return <SidebarMenuItem key={t(item.title as MessageKey)}>
                      <SidebarMenuButton asChild isActive={isActive} className={cn("h-10 rounded-lg transition-all duration-200", "hover:bg-accent/50 active:scale-[0.98]", isActive && "bg-accent/70 font-medium shadow-xs")}>
                        <Link to={item.url} className="flex items-center gap-3 px-3">
                          <item.icon className="h-5 w-5 shrink-0" />
                          <span className="text-[13px]">
                            {t(item.title as MessageKey)}
                          </span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>;
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>

        </SidebarContent>
        
        <SidebarFooter className="p-3 border-t border-border/30 sticky bottom-0 bg-glass-light/80 dark:bg-glass-dark/80 backdrop-blur-md space-y-2 rounded-b-[18px]">
          <LocaleSwitcher className="w-full" />
          <PersistentPodcastPlayer />
          {!currentEpisode && (
            <Suspense fallback={null}>
              <DailyRecapSidebarPlayer />
            </Suspense>
          )}
          {isAuthenticated ? <SidebarMenuButton asChild isActive={location.pathname === "/profile"} className={cn("h-10 rounded-lg transition-all duration-200", "hover:bg-accent/50 active:scale-[0.98]", location.pathname === "/profile" && "bg-accent/70 font-medium shadow-xs")}>
              <Link to="/profile" className="w-full flex items-center gap-3">
                <ProfileAvatar
                  userId={profile?.id}
                  avatarUrl={profile?.avatar_url}
                  name={profile?.full_name || profile?.username}
                  background={profile?.avatar_background}
                  size="xs"
                />

                <span className="text-[13px] truncate">
                  {profile?.full_name || profile?.username || t('nav.profile')}
                </span>
              </Link>
            </SidebarMenuButton> : <Button onClick={() => setShowAuthModal(true)} className="w-full h-9" size="sm" variant="default">
              <span className="text-[13px] font-medium">{t("nav.signIn")}</span>
            </Button>}
        </SidebarFooter>
      </Sidebar>
      </div>
      
      {showAuthModal && (
        <Suspense fallback={null}>
          <AuthModal isOpen onClose={() => setShowAuthModal(false)} />
        </Suspense>
      )}
    </>;
};
export default AppSidebar;